/* ============================================================
   Eiréné | Motion behaviors (vanilla, sans dépendance externe)
   IntersectionObserver natif → reveal au scroll. Aucun risque de blocage.
   Respecte prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal au scroll (fade + translate) ---------- */
  function initReveal(root) {
    if (reduced) {
      root.querySelectorAll('[data-eirene-fade-in], [data-eirene-text-reveal], [data-eirene-image-reveal]')
        .forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const els = root.querySelectorAll('[data-eirene-fade-in], [data-eirene-text-reveal], [data-eirene-image-reveal]');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -5% 0px' });
    els.forEach((el) => {
      if (el.dataset.eireneInit) return;
      el.dataset.eireneInit = '1';
      io.observe(el);
    });
  }

  /* ---------- CountUp léger ---------- */
  function initCountUp(root) {
    root.querySelectorAll('[data-eirene-count-up]').forEach((el) => {
      if (el.dataset.eireneInit) return;
      el.dataset.eireneInit = '1';
      const target = parseFloat(el.dataset.eireneCountUp);
      const from = parseFloat(el.dataset.eireneFrom || 0);
      if (Number.isNaN(target)) return;
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          io.disconnect();
          const start = performance.now();
          const duration = 1600;
          function tick(now) {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            el.textContent = Math.round(from + (target - from) * eased);
            if (p < 1) requestAnimationFrame(tick);
          }
          requestAnimationFrame(tick);
        }
      }, { threshold: 0.5 });
      io.observe(el);
    });
  }

  /* ---------- Header sticky shrink ---------- */
  function initStickyHeader() {
    const header = document.querySelector('[data-eirene-header]');
    if (!header) return;
    const section = header.closest('.shopify-section') || header;
    let last = 0;
    let ticking = false;
    function onScroll() {
      const y = window.scrollY;
      if (!ticking) {
        requestAnimationFrame(() => {
          header.classList.toggle('is-scrolled', y > 24);
          // Hide on scroll down past 200px, show on scroll up (delta > 4px pour éviter jitter iOS momentum)
          const delta = y - last;
          if (y > 200 && delta > 4) {
            section.classList.add('eirene-header--hidden');
          } else if (delta < -4 || y <= 200) {
            section.classList.remove('eirene-header--hidden');
          }
          last = y;
          ticking = false;
        });
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Burger menu mobile ---------- */
  function initBurger() {
    const burger = document.querySelector('[data-eirene-burger]');
    const nav = document.querySelector('.eirene-header__nav');
    if (!burger || !nav) return;
    const close = () => {
      burger.setAttribute('aria-expanded', 'false');
      nav.classList.remove('is-open');
    };
    burger.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = burger.getAttribute('aria-expanded') === 'true';
      burger.setAttribute('aria-expanded', String(!open));
      nav.classList.toggle('is-open', !open);
    });
    nav.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));
    document.addEventListener('click', (e) => {
      if (nav.classList.contains('is-open') && !nav.contains(e.target) && !burger.contains(e.target)) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) close();
    });
  }

  /* ---------- Cart AJAX (quantity update + remove, dynamic totals) ---------- */
  function initCart() {
    const form = document.querySelector('[data-eirene-cart-form]');
    if (!form) return;
    const section = form.closest('[data-eirene-section="cart"]');
    const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

    async function updateCart(lineOrKey, qty) {
      const body = new FormData();
      // Préférer 'line' (index) qui est plus robuste que 'id' avec les line keys encodés
      if (typeof lineOrKey === 'number' || /^\d+$/.test(String(lineOrKey))) {
        body.append('line', String(lineOrKey));
      } else {
        body.append('id', String(lineOrKey));
      }
      body.append('quantity', String(qty));
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.error('[eirene-cart-change] error', res.status, err);
          return null;
        }
        return res.json();
      } catch (e) {
        console.error('[eirene-cart-change] network error', e);
        return null;
      }
    }

    async function refreshSection() {
      // Essaie d'abord Section Rendering API ; fallback sur reload si ça fail
      const endpoints = ['/cart?sections=main', '/cart?section_id=main', '/cart'];
      for (const url of endpoints) {
        try {
          const r = await fetch(url, { headers: { Accept: 'text/html' } });
          if (!r.ok) continue;
          const html = await r.text();
          // Section Rendering API renvoie JSON parfois
          let parsedHtml = html;
          if (html.trim().startsWith('{')) {
            try {
              const json = JSON.parse(html);
              parsedHtml = json.main || Object.values(json)[0] || html;
            } catch {}
          }
          const doc = new DOMParser().parseFromString(parsedHtml, 'text/html');
          const fresh = doc.querySelector('[data-eirene-section="cart"]');
          if (!fresh) continue;
          if (section && section.parentNode) {
            section.replaceWith(fresh);
            initCart();
          }
          return true;
        } catch (e) {
          console.error('[eirene-cart-refresh]', url, 'failed', e);
        }
      }
      // Dernier recours : reload full page
      console.warn('[eirene-cart-refresh] all endpoints failed, reloading');
      window.location.reload();
      return false;
    }

    function refreshTotals(cart) {
      if (!cart) return;
      const moneyFmt = (cents) => {
        const euros = (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return euros.replace(/,00$/, '') + ' €';
      };
      const sub = section.querySelector('[data-cart-subtotal]');
      const tot = section.querySelector('[data-cart-total]');
      if (sub) sub.textContent = moneyFmt(cart.items_subtotal_price);
      if (tot) tot.textContent = moneyFmt(cart.total_price);
      cart.items.forEach((item) => {
        const line = section.querySelector(`[data-cart-key="${item.key}"]`);
        if (!line) return;
        const priceEl = line.querySelector('[data-line-price]');
        if (priceEl) priceEl.textContent = moneyFmt(item.final_line_price);
      });
      // Header badge
      document.querySelectorAll('[data-eirene-cart-count]').forEach((b) => {
        b.textContent = cart.item_count;
        b.classList.add('is-bump');
        setTimeout(() => b.classList.remove('is-bump'), 400);
      });
    }

    // Quantity +/- buttons
    section.addEventListener('click', async (e) => {
      const dec = e.target.closest('[data-qty-dec]');
      const inc = e.target.closest('[data-qty-inc]');
      const rm = e.target.closest('[data-cart-remove]');
      if (dec || inc) {
        e.preventDefault();
        const input = (dec || inc).parentElement.querySelector('[data-qty-input]');
        if (!input) return;
        const cur = parseInt(input.value, 10) || 0;
        const next = Math.max(0, inc ? cur + 1 : cur - 1);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (rm) {
        e.preventDefault();
        const key = rm.dataset.cartKey;
        const lineIdx = rm.dataset.cartLineIndex;
        const line = section.querySelector(`[data-cart-key="${key}"]`);
        if (line) line.style.opacity = '0.4';
        const cart = await updateCart(lineIdx || key, 0);
        if (!cart) {
          if (line) line.style.opacity = '1';
          return;
        }
        updateHeaderBadge(cart.item_count);
        await refreshSection();
      }
    });

    // Debounced quantity input change
    const onQtyChange = debounce(async (input) => {
      const key = input.dataset.cartKey;
      const lineIdx = input.dataset.cartLineIndex;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      const cart = await updateCart(lineIdx || key, qty);
      if (!cart) return;
      updateHeaderBadge(cart.item_count);
      await refreshSection();
    }, 350);

    function updateHeaderBadge(count) {
      document.querySelectorAll('[data-eirene-cart-count]').forEach((b) => {
        b.textContent = count;
        b.classList.add('is-bump');
        setTimeout(() => b.classList.remove('is-bump'), 400);
      });
    }
    section.querySelectorAll('[data-qty-input]').forEach((input) => {
      input.addEventListener('change', () => onQtyChange(input));
    });
  }

  /* ---------- Copy link (article share) ---------- */
  function initCopyLink() {
    document.querySelectorAll('[data-eirene-copy-link]').forEach((btn) => {
      if (btn.dataset.eireneInit) return;
      btn.dataset.eireneInit = '1';
      btn.addEventListener('click', async () => {
        const url = window.location.href;
        try {
          await navigator.clipboard.writeText(url);
        } catch (e) {
          const ta = document.createElement('textarea');
          ta.value = url;
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); } catch (_) {}
          document.body.removeChild(ta);
        }
        btn.classList.add('is-copied');
        setTimeout(() => btn.classList.remove('is-copied'), 1500);
      });
    });
  }

  /* ---------- Accordion "Six portes" — click-to-toggle on mobile (hover reste desktop via CSS) ---------- */
  function initAccordion(root) {
    const scope = root || document;
    const panels = scope.querySelectorAll('.eirene-accordion__panel');
    if (!panels.length) return;

    const mq = window.matchMedia('(max-width: 860px)');

    panels.forEach((panel) => {
      if (panel.dataset.eireneAccordionInit) return;
      panel.dataset.eireneAccordionInit = '1';

      panel.addEventListener('click', (e) => {
        if (!mq.matches) return; // desktop : laisse le hover gérer
        // Ne pas intercepter si click direct sur le CTA (lien)
        if (e.target.closest('a[href]')) return;

        const isOpen = panel.classList.contains('is-open');
        panels.forEach((p) => p.classList.remove('is-open'));
        if (!isOpen) panel.classList.add('is-open');
      });

      // Close open panel on Escape
      panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') panel.classList.remove('is-open');
      });
    });

    // Close all when breakpoint changes (desktop → mobile or inverse)
    if (!window.__eireneAccordionMQ) {
      window.__eireneAccordionMQ = true;
      mq.addEventListener('change', () => {
        document.querySelectorAll('.eirene-accordion__panel.is-open').forEach((p) => p.classList.remove('is-open'));
      });
    }
  }

  /* ---------- Init global défensif ---------- */
  function initAll(root) {
    try { initReveal(root || document); } catch (e) { console.warn('[eirene-motion] reveal', e); }
    try { initCountUp(root || document); } catch (e) { console.warn('[eirene-motion] countup', e); }
    try { initCopyLink(); } catch (e) { console.warn('[eirene-motion] copy-link', e); }
    try { initAccordion(root || document); } catch (e) { console.warn('[eirene-motion] accordion', e); }
  }

  function boot() {
    document.documentElement.classList.add('js-eirene');
    initAll(document);
    try { initStickyHeader(); } catch (e) {}
    try { initBurger(); } catch (e) {}
    try { initCart(); } catch (e) { console.warn('[eirene-motion] cart', e); }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor : ré-init au chargement de section
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));

  window.eireneMotion = { initAll };
})();
